import React, { useContext, useEffect, useRef } from 'react';
import { func, number, string, bool } from 'prop-types';
import YouTube from 'react-youtube';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';

import { WarningIcon } from 'components/Icon';
import Button from 'components/Button';
import ApplicationContext from 'components/ApplicationContext';

import styles from './InstructorCard.scss';

function InstructorCard({
  liveTopicCount,
  courseCount,
  askableTutorId,
  name,
  avatarUrl,
  subjectString,
  onCtaClick,
  active,
  showTopicCount,
  showCourseCount,
  showScrollToContentButton,
  videoId,
  videoSource,
}) {
  useStyles(styles);
  const { t } = useTranslation(['instructorProfile']);
  const playerRef = useRef(null);
  const {
    screenWidth: { isWideDesktop },
  } = useContext(ApplicationContext);

  const youtubeOptions = {
    height: '100%',
    width: '100%',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 1,
      modestbranding: 1,
    },
  };

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }
    playerRef.current.internalPlayer.mute();
  }, [playerRef]);

  return (
    <div
      className={cx(styles.root, {
        [styles.videoRoot]: !!videoId,
        [styles.avatarRoot]: !videoId,
      })}
    >
      {videoSource === 'youtube' && !isWideDesktop ? (
        <YouTube
          ref={playerRef}
          opts={youtubeOptions}
          containerClassName={styles.player}
          videoId={videoId}
        />
      ) : (
        <div className={styles.avatarWrapper}>
          <img src={avatarUrl} alt={name} className={styles.avatar} />
        </div>
      )}
      <div>
        {!active && (
          <div className={styles.disableHintTablet}>
            <div>
              <WarningIcon width={24} />
            </div>
            <p className={styles.text}>
              {t('instructorProfile.retiredStatus')}
            </p>
          </div>
        )}
        <div className={styles.content}>
          <div className={styles.info}>
            <h3 className={styles.title}>{name}</h3>
            <p className={styles.subjects}>{subjectString}</p>
            <ul className={styles.lessonCount}>
              {showTopicCount && (
                <li className={styles.lessonCountItem}>
                  <span className={styles.count}>{liveTopicCount}</span>
                  <span>
                    {t('instructorProfile.regularClass', {
                      count: liveTopicCount,
                    })}
                  </span>
                </li>
              )}
              {showCourseCount && (
                <li className={styles.lessonCountItem}>
                  <span className={styles.count}>{courseCount}</span>
                  <span>
                    {t('instructorProfile.course', { count: courseCount })}
                  </span>
                </li>
              )}
            </ul>

            {askableTutorId && (
              <p className={styles.qaOffering}>
                {t('instructorProfile.qaAvailable')}
              </p>
            )}
          </div>

          {showScrollToContentButton && (
            <Button
              type="primary"
              className={styles.instructorLessonCta}
              text={t(`instructorProfile.instructorCardCta`)}
              onClick={onCtaClick}
            />
          )}
        </div>
      </div>
      {!active && (
        <div className={styles.disableHintDesktop}>
          <div>
            <WarningIcon width={24} />
          </div>
          <p className={styles.text}>{t('instructorProfile.retiredStatus')}</p>
        </div>
      )}
    </div>
  );
}
InstructorCard.propTypes = {
  name: string.isRequired,
  subjectString: string.isRequired,
  avatarUrl: string.isRequired,
  active: bool.isRequired,
  askableTutorId: number,
  liveTopicCount: number.isRequired,
  courseCount: number.isRequired,
  onCtaClick: func.isRequired,
  showTopicCount: bool.isRequired,
  showCourseCount: bool.isRequired,
  showScrollToContentButton: bool.isRequired,
  videoId: string,
  videoSource: string,
};

InstructorCard.defaultProps = {
  askableTutorId: undefined,
  videoId: undefined,
  videoSource: undefined,
};

export default InstructorCard;
